import { useRef, Fragment } from "react";
import { motion, useReducedMotion, useMotionValue, useSpring } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

/* Block fade-up reveal when scrolled into view */
export const Reveal = ({
  children,
  className,
  y = 30,
  delay = 0,
  amount = 0.18,
  once = true,
  ...rest
}) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount }}
      transition={{ duration: 0.75, ease: EASE, delay }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

/* Parent that staggers its StaggerItem children on scroll-in */
export const Stagger = ({ children, className, gap = 0.09, amount = 0.15, once = true, ...rest }) => (
  <motion.div
    className={className}
    initial="hidden"
    whileInView="show"
    viewport={{ once, amount }}
    variants={{ hidden: {}, show: { transition: { staggerChildren: gap } } }}
    {...rest}
  >
    {children}
  </motion.div>
);

const itemVariants = (y, reduce) => ({
  hidden: reduce ? { opacity: 0 } : { opacity: 0, y },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } },
});

/* A staggered child rendered as any motion element (div, a, ...) */
export const StaggerItem = ({ children, className, y = 34, as = "div", ...rest }) => {
  const reduce = useReducedMotion();
  const M = motion[as] || motion.div;
  return (
    <M className={className} variants={itemVariants(y, reduce)} {...rest}>
      {children}
    </M>
  );
};

/* Masked, word-by-word title reveal for hero headlines */
export const MaskedTitle = ({ text, className, testid = "hero-title" }) => {
  const reduce = useReducedMotion();
  if (reduce) {
    return (
      <h1 className={className} data-testid={testid}>
        {text}
      </h1>
    );
  }
  const words = text.split(" ");
  return (
    <h1 className={className} data-testid={testid} aria-label={text}>
      {words.map((w, i) => (
        <Fragment key={`${w}-${i}`}>
          <span className="mask-line" aria-hidden="true">
            <motion.span
              className="mask-word"
              initial={{ y: "115%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.85, ease: EASE, delay: 0.12 + i * 0.075 }}
            >
              {w}
            </motion.span>
          </span>
          {i < words.length - 1 ? " " : ""}
        </Fragment>
      ))}
    </h1>
  );
};

/* Cursor-magnetic anchor button with hover/tap spring */
export const MagneticButton = ({ href, className, children, strength = 0.3, ...rest }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const mvx = useMotionValue(0);
  const mvy = useMotionValue(0);
  const x = useSpring(mvx, { stiffness: 220, damping: 18, mass: 0.4 });
  const y = useSpring(mvy, { stiffness: 220, damping: 18, mass: 0.4 });

  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mvx.set((e.clientX - (r.left + r.width / 2)) * strength);
    mvy.set((e.clientY - (r.top + r.height / 2)) * strength * 1.3);
  };
  const reset = () => {
    mvx.set(0);
    mvy.set(0);
  };

  return (
    <motion.a
      ref={ref}
      href={href}
      className={className}
      style={{ x, y }}
      onMouseMove={onMove}
      onMouseLeave={reset}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.97 }}
      {...rest}
    >
      {children}
    </motion.a>
  );
};
